import { useState, useMemo, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { X, ZoomIn, ChevronLeft, ChevronRight, Phone, Mail } from "lucide-react";
import { handleWhatsAppRedirect } from "../helper/whatsapp";
import { GALLERY_IMAGES, GALLERY_CATEGORIES } from "../data/gallery";

const Gallery = () => {
  const [activeCategory, setActiveCategory] = useState("All");
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  // Filtered images
  const filteredImages = useMemo(() => {
    if (activeCategory === "All") return GALLERY_IMAGES;
    return GALLERY_IMAGES.filter((img) => img.category === activeCategory);
  }, [activeCategory]);

  // Lightbox controls
  const openLightbox = useCallback((index: number) => {
    setLightboxIndex(index);
    document.body.style.overflow = "hidden";
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxIndex(null);
    document.body.style.overflow = "auto";
  }, []);

  const goNext = useCallback(() => {
    if (lightboxIndex === null) return;
    setLightboxIndex((prev) =>
      prev !== null && prev < filteredImages.length - 1 ? prev + 1 : 0
    );
  }, [lightboxIndex, filteredImages.length]);

  const goPrev = useCallback(() => {
    if (lightboxIndex === null) return;
    setLightboxIndex((prev) =>
      prev !== null && prev > 0 ? prev - 1 : filteredImages.length - 1
    );
  }, [lightboxIndex, filteredImages.length]);

  // Keyboard controls
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") goNext();
      if (e.key === "ArrowLeft") goPrev();
    },
    [closeLightbox, goNext, goPrev]
  );

  return (
    <>
      <Helmet>
        <title>Gallery | SS Plastic – FRP Manufacturer, Vapi Gujarat</title>
        <meta
          name="description"
          content="View our gallery of FRP tanks, scrubbers, pipes, ductings, and industrial vessels manufactured at SS Plastic, Vapi, Gujarat."
        />
        <meta property="og:title" content="Gallery | SS Plastic" />
        <meta property="og:description" content="View our gallery of FRP tanks, scrubbers, pipes, ductings, and industrial vessels manufactured at SS Plastic, Vapi, Gujarat." />
        <meta property="og:image" content="/ssplasticlogo.webp" />
        <meta property="og:url" content="https://www.ss-plastic.com/gallery" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Gallery | SS Plastic" />
        <meta name="twitter:description" content="View our gallery of FRP tanks, scrubbers, pipes, ductings, and industrial vessels manufactured at SS Plastic, Vapi, Gujarat." />
        <meta name="twitter:image" content="/ssplasticlogo.webp" />
        <link rel="canonical" href="https://www.ss-plastic.com/gallery" />
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Hero */}
        <section className="bg-gradient-to-br from-egyptian-blue to-violet-blue text-white py-14 sm:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="font-display font-bold text-3xl sm:text-5xl mb-4">
              Our Gallery
            </h1>
            <p className="text-base sm:text-lg max-w-2xl mx-auto text-white/90">
              Explore our manufacturing facility, finished products, and
              on-site installations across India.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
              <a
                href="tel:+919825850501"
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/30 rounded-full px-5 py-2 text-sm transition-colors"
              >
                <Phone className="h-4 w-4" /> +91 98258 50501
              </a>
              <a
                href="mailto:info@ss-plastic.com"
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/30 rounded-full px-5 py-2 text-sm transition-colors"
              >
                <Mail className="h-4 w-4" /> info@ss-plastic.com
              </a>
            </div>
          </div>
        </section>

        {/* Category Filter */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-8">
            {GALLERY_CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-full text-sm font-medium border transition-all duration-300 ${
                  activeCategory === cat
                    ? "bg-egyptian-blue text-white border-egyptian-blue shadow-lg shadow-egyptian-blue/30"
                    : "bg-white text-gray-600 border-gray-300 hover:border-egyptian-blue hover:text-egyptian-blue"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Count */}
          <p className="text-sm text-gray-500 text-center mb-6">
            {filteredImages.length} image{filteredImages.length !== 1 ? "s" : ""}
            {activeCategory !== "All" && ` in ${activeCategory}`}
          </p>

          {/* Gallery Grid */}
          {filteredImages.length === 0 ? (
            <div className="text-center py-16">
              <div className="text-5xl mb-4">📷</div>
              <p className="text-gray-500 text-lg">No images in this category</p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => setActiveCategory("All")}
              >
                Show All
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
              {filteredImages.map((image, index) => (
                <div
                  key={image.id}
                  className="group relative rounded-xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 cursor-pointer bg-gray-100 aspect-[4/3]"
                  onClick={() => openLightbox(index)}
                >
                  {/* Image */}
                  <img
                    src={image.src}
                    alt={image.alt}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                  />

                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-5">
                    <h3 className="text-white font-semibold text-base mb-1">
                      {image.title}
                    </h3>
                    <span className="text-amber text-sm font-medium">
                      {image.category}
                    </span>
                  </div>

                  {/* Zoom icon */}
                  <div className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 border border-white/30">
                    <ZoomIn className="h-4 w-4 text-white" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* CTA */}
        <section className="py-12 sm:py-16 bg-gradient-to-br from-egyptian-blue to-violet-blue text-white px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-display font-bold text-2xl sm:text-3xl mb-4">
              Like What You See?
            </h2>
            <p className="text-base sm:text-lg mb-8 text-white/90">
              Get a custom quote for your FRP requirements. Our team is ready
              to help you with design, fabrication, and delivery.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button variant="action" size="xl" asChild>
                <Link to="/contact">Get a Quote</Link>
              </Button>
              <Button
                variant="trust"
                size="xl"
                onClick={handleWhatsAppRedirect}
              >
                WhatsApp Us
              </Button>
            </div>
          </div>
        </section>

        {/* Floating WhatsApp */}
        <div className="fixed bottom-3 right-3 sm:bottom-6 sm:right-6 z-50">
          <Button
            size="sm"
            className="shadow-xl bg-green-600 hover:bg-green-700"
            onClick={handleWhatsAppRedirect}
          >
            <svg
              style={{ width: "20px", height: "20px" }}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 128 128"
            >
              <path
                fill="white"
                d="M 64 2 C 29.8 2 2 29.8 2 64 C 2 74.5 4.5992188 84.800391 9.6992188 93.900391 L 4.4003906 113.30078 C 3.5003906 116.40078 4.3992188 119.60039 6.6992188 121.90039 C 8.9992188 124.20039 12.200781 125.10078 15.300781 124.30078 L 35.5 119 C 44.3 123.6 54.099609 126 64.099609 126 C 98.299609 126 126.09961 98.2 126.09961 64 C 126.09961 47.4 119.7 31.899219 108 20.199219 C 96.2 8.4992187 80.6 2 64 2 z M 64 8 C 79 8 93.099609 13.800391 103.59961 24.400391 C 114.19961 35.000391 120.1 49.1 120 64 C 120 94.9 94.9 120 64 120 C 54.7 120 45.399219 117.59922 37.199219 113.19922 C 36.799219 112.99922 36.300781 112.80078 35.800781 112.80078 C 35.500781 112.80078 35.3 112.80039 35 112.90039 L 13.699219 118.5 C 12.199219 118.9 11.200781 118.09922 10.800781 117.69922 C 10.400781 117.29922 9.6 116.30078 10 114.80078 L 15.599609 94.199219 C 15.799609 93.399219 15.700781 92.600391 15.300781 91.900391 C 10.500781 83.500391 8 73.8 8 64 C 8 33.1 33.1 8 64 8 z M 64 17 C 38.1 17 17 38 17 64 C 17 72.3 19.200781 80.4 23.300781 87.5 C 24.900781 90.3 25.3 93.599219 24.5 96.699219 L 21.599609 107.19922 L 32.800781 104.30078 C 33.800781 104.00078 34.800781 103.90039 35.800781 103.90039 C 37.800781 103.90039 39.8 104.40039 41.5 105.40039 C 48.4 109.00039 56.1 111 64 111 C 89.9 111 111 89.9 111 64 C 111 51.4 106.09922 39.599219 97.199219 30.699219 C 88.399219 21.899219 76.6 17 64 17 z M 43.099609 36.699219 L 45.900391 36.699219 C 47.000391 36.699219 48.099219 36.799219 49.199219 39.199219 C 50.499219 42.099219 53.399219 49.399609 53.699219 50.099609 C 54.099219 50.799609 54.300781 51.699219 53.800781 52.699219 C 53.300781 53.699219 53.100781 54.299219 52.300781 55.199219 C 51.600781 56.099219 50.699609 57.100781 50.099609 57.800781 C 49.399609 58.500781 48.6 59.300781 49.5 60.800781 C 50.4 62.300781 53.299219 67.1 57.699219 71 C 63.299219 76 68.099609 77.600781 69.599609 78.300781 C 71.099609 79.000781 71.900781 78.900391 72.800781 77.900391 C 73.700781 76.900391 76.5 73.599609 77.5 72.099609 C 78.5 70.599609 79.500781 70.900391 80.800781 71.400391 C 82.200781 71.900391 89.400391 75.499219 90.900391 76.199219 C 92.400391 76.899219 93.399219 77.300391 93.699219 77.900391 C 94.099219 78.700391 94.100391 81.599609 92.900391 85.099609 C 91.700391 88.499609 85.700391 91.899609 82.900391 92.099609 C 80.200391 92.299609 77.699219 93.300391 65.199219 88.400391 C 50.199219 82.500391 40.7 67.099609 40 66.099609 C 39.3 65.099609 34 58.100781 34 50.800781 C 34 43.500781 37.799219 40 39.199219 38.5 C 40.599219 37 42.099609 36.699219 43.099609 36.699219 z"
              />
            </svg>
            <span className="ml-2">Quick Quote</span>
          </Button>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <div
          className="fixed inset-0 z-[999] bg-black/95 flex items-center justify-center"
          onClick={closeLightbox}
          onKeyDown={handleKeyDown}
          tabIndex={0}
        >
          {/* Close button */}
          <button
            className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/10 border border-white/20 backdrop-blur-md flex items-center justify-center hover:bg-white/20 transition-all"
            onClick={closeLightbox}
          >
            <X className="h-5 w-5 text-white" />
          </button>

          {/* Prev */}
          <button
            className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white/10 border border-white/20 backdrop-blur-md flex items-center justify-center hover:bg-white/20 transition-all"
            onClick={(e) => {
              e.stopPropagation();
              goPrev();
            }}
          >
            <ChevronLeft className="h-5 w-5 text-white" />
          </button>

          {/* Next */}
          <button
            className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white/10 border border-white/20 backdrop-blur-md flex items-center justify-center hover:bg-white/20 transition-all"
            onClick={(e) => {
              e.stopPropagation();
              goNext();
            }}
          >
            <ChevronRight className="h-5 w-5 text-white" />
          </button>

          {/* Image */}
          <div
            className="max-w-5xl max-h-[85vh] mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={filteredImages[lightboxIndex].src}
              alt={filteredImages[lightboxIndex].alt}
              className="max-w-full max-h-[75vh] object-contain mx-auto rounded-lg shadow-2xl"
            />
            <div className="text-center mt-4">
              <h3 className="text-white font-semibold text-lg">
                {filteredImages[lightboxIndex].title}
              </h3>
              <p className="text-amber text-sm mt-1">
                {filteredImages[lightboxIndex].category}
              </p>
              <p className="text-white/50 text-xs mt-2">
                {lightboxIndex + 1} / {filteredImages.length}
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Gallery;